import { useEffect, useId, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CalendarClock, CheckCircle2, ChevronDown, ChevronUp, Eye, EyeOff, ListTodo, MessageSquare, RotateCcw } from 'lucide-react';
import Button from '../ui/Button';
import { CrmFileList, PhotoPicker } from './CrmPhotos';
import { uploadAll } from './crmUpload';
import salesService from '../../services/sales';
import { errorMessage } from '../../utils/apiResponse';
import { formatDateTimeInTz, formatLocalDateInTz } from '../../utils/timezone';
import { categoryLabel, subjectLinks } from '../../utils/crm';
import type { FollowUp, FollowUpChanges, FollowUpDetail, SalesPerson } from '../../types/sales';

/**
 * One follow-up or issue: what, about what (customer, order, line, load), by
 * when and who has it. The person it is given to, whoever made it, or a
 * Sales Admin can give it to someone else, move its day, mark it done with
 * what was done, or open it again. Anyone can open its thread, write on it
 * with photos, and watch it; everyone on it is told of each update.
 */
export default function FollowUpCard({ fu, people, tz, onChange, showing = {}, startOpen = false }: {
  fu: FollowUp; people: SalesPerson[]; tz: string; onChange: (fu: FollowUp) => void;
  /** What the page is already about, so the card does not name it again. */
  showing?: { customer?: boolean; order?: boolean; load?: boolean };
  /** Opened from a notification: the thread shows at once. */
  startOpen?: boolean;
}) {
  const id = useId();
  const [closing, setClosing] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(startOpen);
  const [detail, setDetail] = useState<FollowUpDetail | null>(null);
  const [reply, setReply] = useState('');
  const [replyPhotos, setReplyPhotos] = useState<File[]>([]);

  const loadDetail = async () => {
    try {
      const res = await salesService.getFollowUp(fu.id);
      if (!res.success || !res.data) throw res;
      setDetail(res.data);
    } catch (err) {
      setError(errorMessage(err, 'Could not load the updates'));
    }
  };
  useEffect(() => { if (open) loadDetail(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (fn: () => Promise<{ success: boolean; data?: FollowUp }>) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fn();
      if (!res.success || !res.data) throw res;
      onChange({ ...fu, ...res.data });
      return true;
    } catch (err) {
      setError(errorMessage(err, 'Could not save'));
      return false;
    } finally {
      setBusy(false);
    }
  };
  const change = (changes: FollowUpChanges) => act(() => salesService.updateFollowUp(fu.id, changes));

  const watching = detail?.watching ?? fu.watching ?? false;
  const toggleWatch = async () => {
    setBusy(true);
    try {
      const res = await salesService.setWatching(fu.id, !watching);
      if (!res.success) throw res;
      onChange({ ...fu, watching: !watching });
      if (detail) setDetail({ ...detail, watching: !watching });
    } catch (err) {
      setError(errorMessage(err, 'Could not save'));
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async (e: FormEvent) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await salesService.addUpdate(fu.id, reply.trim());
      if (!res.success || !res.data) throw res;
      const { failed } = await uploadAll('updates', res.data.id, replyPhotos);
      if (failed) setError(`Sent, but ${failed === 1 ? 'a photo' : `${failed} photos`} could not be.`);
      setReply('');
      setReplyPhotos([]);
      onChange({ ...fu, update_count: fu.update_count + 1, watching: true });
      await loadDetail();
    } catch (err) {
      setError(errorMessage(err, 'Could not send the update'));
    } finally {
      setBusy(false);
    }
  };

  const due = formatLocalDateInTz(fu.due_on, tz, { weekday: 'short', day: 'numeric', month: 'short' });
  const tone = fu.status === 'done' ? '' : fu.overdue ? 'bg-red-50' : fu.due_today ? 'bg-amber-50' : '';
  const isIssue = fu.kind === 'issue';
  const links = subjectLinks(fu, showing);
  const Icon = isIssue ? AlertTriangle : ListTodo;
  const updates = detail?.updates.length ?? fu.update_count;

  return (
    <li className={`p-4 min-w-0 ${tone}`}>
      <div className="flex items-start gap-3 min-w-0">
        <Icon className={`h-5 w-5 shrink-0 ${fu.status === 'done' ? 'text-neutral-400' : isIssue ? 'text-red-600' : 'text-primary-600'}`} aria-hidden />
        <div className="min-w-0 flex-1 space-y-2">
          <div>
            <p className="text-sm break-words">
              {isIssue && (
                <span className="mr-2 inline-block rounded-md bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-800">{categoryLabel(fu.category)}</span>
              )}
              <span className={`font-semibold ${fu.status === 'done' ? 'text-neutral-500 line-through' : 'text-neutral-900'}`}>{fu.title}</span>
            </p>
            {(links.length > 0 || fu.phone) && (
              <p className="text-xs text-neutral-600 break-words">
                {links.map((l, i) => (
                  <span key={`${l.label}-${i}`}>
                    {i > 0 && ' · '}
                    {l.to ? <Link to={l.to} className="font-medium text-primary-700 underline">{l.label}</Link> : l.label}
                  </span>
                ))}
                {links.length > 0 && fu.phone ? ' · ' : ''}
                {fu.phone && <a href={`tel:${fu.phone.replace(/[^\d+]/g, '')}`} className="text-primary-700 underline">{fu.phone}</a>}
              </p>
            )}
            <p className="text-xs text-neutral-600 break-words">
              <CalendarClock className="h-3.5 w-3.5 inline mr-1" aria-hidden />
              {fu.status === 'open' && fu.overdue && <span className="font-medium text-red-700">Overdue · </span>}
              {fu.status === 'open' && fu.due_today && <span className="font-medium text-amber-800">Today · </span>}
              By {due} · {fu.assigned_to_name}
              {fu.created_by_name && fu.created_by_user_id !== fu.assigned_to_user_id ? ` · from ${fu.created_by_name}` : ''}
              {fu.voicemail_id ? ' · from a voicemail' : fu.call_id ? ' · from a call' : ''}
            </p>
          </div>
          {fu.details && <p className="text-sm text-neutral-700 whitespace-pre-wrap break-words">{fu.details}</p>}
          <CrmFileList files={fu.files ?? []} />

          {fu.status === 'done' ? (
            <div className="text-sm text-neutral-600 break-words">
              <CheckCircle2 className="h-4 w-4 inline text-emerald-600 mr-1" aria-hidden />
              {isIssue ? 'Resolved' : 'Done'}{fu.done_by_name ? ` by ${fu.done_by_name}` : ''}{fu.done_at ? `, ${formatDateTimeInTz(fu.done_at, tz)}` : ''}
              {fu.done_note ? `: ${fu.done_note}` : ''}
              {fu.can_act && (
                <Button size="sm" variant="ghost" className="ml-2" disabled={busy} onClick={() => act(() => salesService.reopenFollowUp(fu.id))}>
                  <RotateCcw className="h-4 w-4 mr-1" aria-hidden />Open again
                </Button>
              )}
            </div>
          ) : fu.can_act ? (
            <div className="flex flex-col sm:flex-row sm:items-end gap-2 min-w-0 flex-wrap">
              <div className="min-w-0">
                <label htmlFor={`${id}-who`} className="block text-xs font-medium text-neutral-600 mb-1">Given to</label>
                <select id={`${id}-who`} value={fu.assigned_to_user_id} disabled={busy}
                  onChange={(e) => change({ assigned_to_user_id: Number(e.target.value) })}
                  className="w-full sm:w-52 rounded-lg border border-neutral-300 px-3 py-2 text-sm min-w-0">
                  {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  {!people.some((p) => p.id === fu.assigned_to_user_id) && <option value={fu.assigned_to_user_id}>{fu.assigned_to_name}</option>}
                </select>
              </div>
              <div className="min-w-0">
                <label htmlFor={`${id}-due`} className="block text-xs font-medium text-neutral-600 mb-1">By</label>
                <input id={`${id}-due`} type="date" value={fu.due_on} disabled={busy}
                  onChange={(e) => { if (e.target.value) change({ due_on: e.target.value }); }}
                  className="w-full sm:w-44 rounded-lg border border-neutral-300 px-3 py-2 text-sm min-w-0" />
              </div>
              {closing ? (
                <div className="flex flex-col sm:flex-row sm:items-end gap-2 flex-1 min-w-0">
                  <div className="flex-1 min-w-0">
                    <label htmlFor={`${id}-note`} className="block text-xs font-medium text-neutral-600 mb-1">What was done (optional)</label>
                    <input id={`${id}-note`} value={note} onChange={(e) => setNote(e.target.value)}
                      placeholder={isIssue ? 'Credited three trees' : 'Rang, sent the price list'}
                      className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm min-w-0" />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" isLoading={busy} onClick={async () => { if (await act(() => salesService.completeFollowUp(fu.id, note.trim()))) setClosing(false); }}>
                      Save
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setClosing(false)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <Button size="sm" variant="outline" onClick={() => setClosing(true)}>
                  <CheckCircle2 className="h-4 w-4 mr-1" aria-hidden />{isIssue ? 'Resolved' : 'Done'}
                </Button>
              )}
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-1">
            <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
              <MessageSquare className="h-4 w-4 mr-1" aria-hidden />
              {updates ? `Updates (${updates})` : 'Write an update'}
              {open ? <ChevronUp className="h-4 w-4 ml-1" aria-hidden /> : <ChevronDown className="h-4 w-4 ml-1" aria-hidden />}
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={toggleWatch} aria-pressed={watching}>
              {watching ? <><EyeOff className="h-4 w-4 mr-1" aria-hidden />Stop watching</> : <><Eye className="h-4 w-4 mr-1" aria-hidden />Watch</>}
            </Button>
          </div>

          {open && (
            <div className="space-y-3 border-l-2 border-neutral-200 pl-3 min-w-0">
              {detail?.updates.map((u) => (
                <div key={u.id} className="space-y-1 min-w-0">
                  <p className="text-xs text-neutral-600">{u.created_by_name || 'Someone'}, {formatDateTimeInTz(u.created_at, tz)}</p>
                  <p className="text-sm text-neutral-800 whitespace-pre-wrap break-words">{u.body}</p>
                  <CrmFileList files={u.files} />
                </div>
              ))}
              <form onSubmit={sendReply} className="space-y-2 min-w-0">
                <label htmlFor={`${id}-reply`} className="block text-xs font-medium text-neutral-600">Update</label>
                <textarea id={`${id}-reply`} value={reply} onChange={(e) => setReply(e.target.value)} rows={2} maxLength={4000}
                  placeholder="What happened since, what was agreed"
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm min-w-0" />
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <PhotoPicker files={replyPhotos} onChange={setReplyPhotos} />
                  <Button type="submit" size="sm" isLoading={busy} disabled={!reply.trim()}>Send update</Button>
                </div>
              </form>
            </div>
          )}
          {error && <p className="text-sm text-red-700 break-words">{error}</p>}
        </div>
      </div>
    </li>
  );
}
